import { useCallback, useEffect, useMemo, useState } from 'react';
import { z } from 'zod';
import useCommonState, {
  type BaseStateProps,
  type MobxProps,
  baseValidationSchema,
  type PersonaTitleFormPayload,
  CommonFormProps,
} from './useCommonState';
import { type Inputs } from './Input';

const titleValidationSchema = baseValidationSchema.extend({
  titles: z.array(z.string()).min(1, 'Select at least one Title.'),
});

const useTitlePersona = ({
  selectedPersona,
  setToastMessageData,
  isPersonaMessageTtlAndPinLockEnabled,
  loadTitles,
  editMode,
  setSelectedPersona,
  updatePersona,
  createPersona,
  setPersonaFormState,
}: BaseStateProps & Omit<MobxProps, 'findSCIMGroups'> & CommonFormProps) => {
  const {
    commonFormFields,
    setCommonFormFields,
    setLoading,
    selectedOrganizationId,
    setTemplate,
    loading,
    userPermissions,
    setUserPermissions,
    userSettings,
    setUserSettings,
    canRevert,
    errors,
    revertChanges,
    setErrors,
    validateForm,
    template,
  } = useCommonState<'title'>({
    selectedPersona,
    setToastMessageData,
    isPersonaMessageTtlAndPinLockEnabled,
  });
  const [selectedTitles, setSelectedTitles] = useState(selectedPersona?.titles || []);
  const [titles, setTitles] = useState<string[]>([]);

  const loadRequirements = useCallback(async () => {
    setLoading(true);
    try {
      const foundTitles = await loadTitles(
        selectedOrganizationId,
        selectedPersona?.personaId || ''
      );
      setTitles(foundTitles);
      setSelectedTitles(selectedPersona?.titles || []);
    } finally {
      setLoading(false);
    }
  }, [
    loadTitles,
    selectedOrganizationId,
    selectedPersona?.titles,
    setLoading,
    selectedPersona?.personaId,
  ]);

  useEffect(() => {
    loadRequirements();
  }, [loadRequirements]);

  const schema = useMemo(() => {
    const schema: Inputs[] = [
      {
        label: 'Name: ',
        type: 'input',
        optionName: 'name',
        dataTestId: 'newPersonaId',
        onInputChange: (value: string) =>
          setCommonFormFields((currentValues) => ({ ...currentValues, name: value })),
        disabled: !editMode,
        id: 'name',
        value: commonFormFields.name,
      },
      {
        label: 'Description: ',
        type: 'input',
        optionName: 'description',
        dataTestId: 'persona description',
        onInputChange: (value: string) =>
          setCommonFormFields((currentValues) => ({ ...currentValues, description: value })),
        disabled: !editMode,
        id: 'description',
        value: commonFormFields.description || '',
      },
      {
        label: 'Title: ',
        optionName: 'titles',
        type: 'multiSelect',
        listOptions: titles.map((title) => ({ label: title, value: title })),
        dataTestId: 'titleDropdown',
        onInputChange: setSelectedTitles,
        disabled: !editMode,
        id: 'titles',
        value: selectedTitles,
        placeholder: 'Select titles...',
      },
      {
        type: 'select',
        label: 'Template: ',
        optionName: 'template',
        dataTestId: 'templateDropdown',
        onInputChange: (template) => setTemplate(template),
        disabled: !editMode,
        id: 'template',
        placeholder: 'Select a template...',
        skip: !!selectedPersona,
      },
    ];
    return schema;
  }, [
    commonFormFields.description,
    commonFormFields.name,
    editMode,
    selectedPersona,
    selectedTitles,
    setCommonFormFields,
    setTemplate,
    titles,
  ]);

  const savePersona = async (persona: PersonaTitleFormPayload) => {
    try {
      const baseFieldsValues = {
        name: persona.name || '',
        description: persona.description || '',
        titles: persona.titles || [],
        personaType: 'TITLE' as const,
      };
      if (selectedPersona) {
        await updatePersona(
          {
            ...baseFieldsValues,
            permissions: userPermissions,
            settings: userSettings,
          },
          selectedPersona.personaId,
          selectedOrganizationId
        );
        setSelectedPersona(undefined);
        setToastMessageData({ type: 'SUCCESS', message: 'Persona updated' });
      } else {
        await createPersona(
          {
            ...baseFieldsValues,
            parentPersonaId: template?.personaId,
            permissions: userPermissions,
            settings: userSettings,
          },
          selectedOrganizationId
        );
        setToastMessageData({ type: 'SUCCESS', message: 'Persona created' });
      }
      setPersonaFormState({ display: false, type: 'title' });
      setErrors({});
    } catch (error) {
      let errorMessage = 'Response error please try again.';
      if (error?.response?.status === 409) {
        errorMessage = error?.response?.text;
      }
      setErrors((prev) => ({ ...prev, general: errorMessage }));
      setToastMessageData({ type: 'FAILURE', message: errorMessage });
    }
  };

  const onSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const success = validateForm(
      {
        name: commonFormFields.name,
        description: commonFormFields.description,
        titles: selectedTitles,
      },
      titleValidationSchema
    );
    if (!success) return;
    savePersona({
      name: commonFormFields.name,
      description: commonFormFields.description,
      titles: selectedTitles,
    });
  };

  return {
    canRevert,
    errors,
    loading,
    onSave,
    revertChanges,
    schema,
    selectedTitles,
    setUserPermissions,
    setUserSettings,
    userPermissions,
    userSettings,
  };
};

export default useTitlePersona;
